import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const CreateDocument = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const preselectedTemplateId = searchParams.get('template');

    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: Select Template, 2: Fill Variables, 3: Preview
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        variable_values: {},
        output_format: 'PDF'
    });
    const [preview, setPreview] = useState('');

    useEffect(() => {
        fetchTemplates();
    }, []);

    useEffect(() => {
        if (preselectedTemplateId && templates.length > 0) {
            const template = templates.find(t => t.id === parseInt(preselectedTemplateId));
            if (template) {
                handleSelectTemplate(template);
            }
        }
    }, [preselectedTemplateId, templates]);

    const fetchTemplates = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/generator/templates/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setTemplates(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching templates:', err);
        }
    };

    const handleSelectTemplate = async (template) => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`/api/generator/templates/${template.id}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setSelectedTemplate(data);

            // Initialize variable values with defaults
            const initialValues = {};
            data.variables_schema?.forEach(v => {
                initialValues[v.name] = v.default || '';
            });

            setFormData(prev => ({
                ...prev,
                title: `${data.name} - ${new Date().toLocaleDateString()}`,
                variable_values: initialValues
            }));

            setStep(2);
        } catch (err) {
            console.error('Error fetching template:', err);
        }
    };

    const handleVariableChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            variable_values: {
                ...prev.variable_values,
                [name]: value
            }
        }));
    };

    const handlePreview = async () => {
        if (!selectedTemplate) return;

        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`/api/generator/templates/${selectedTemplate.id}/preview/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ variable_values: formData.variable_values })
            });
            const data = await response.json();
            setPreview(data.preview);
            setStep(3);
        } catch (err) {
            console.error('Error generating preview:', err);
        }
    };

    const handleGenerate = async () => {
        if (!selectedTemplate) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/generator/documents/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    template_id: selectedTemplate.id,
                    title: formData.title,
                    description: formData.description,
                    variable_values: formData.variable_values,
                    output_format: formData.output_format
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.errors?.join(', ') || 'Generation failed');
            }

            const data = await response.json();
            navigate(`/generator/documents/${data.id}`);
        } catch (err) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    const installSampleTemplates = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/generator/templates/install_samples/', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            alert(data.message);
            fetchTemplates();
        } catch (err) {
            alert('Failed to install sample templates');
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <nav className="text-sm text-gray-500 mb-2">
                    <Link to="/generator" className="hover:text-blue-600">Document Generator</Link>
                    <span className="mx-2">›</span>
                    <span>Create Document</span>
                </nav>
                <h1 className="text-2xl font-bold text-gray-900">Create New Document</h1>
            </div>

            {/* Progress Steps */}
            <div className="mb-8">
                <div className="flex items-center justify-center">
                    {[
                        { num: 1, label: 'Select Template' },
                        { num: 2, label: 'Fill Details' },
                        { num: 3, label: 'Preview & Generate' }
                    ].map((s, i) => (
                        <React.Fragment key={s.num}>
                            <div className="flex flex-col items-center">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${step >= s.num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                                    }`}>
                                    {s.num}
                                </div>
                                <span className="text-sm mt-1 text-gray-600">{s.label}</span>
                            </div>
                            {i < 2 && (
                                <div className={`w-24 h-1 mx-2 ${step > s.num ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* Step 1: Select Template */}
            {step === 1 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold text-gray-900">Choose a Template</h2>
                        <button
                            onClick={installSampleTemplates}
                            className="text-sm text-blue-600 hover:text-blue-700"
                        >
                            Install Sample Templates
                        </button>
                    </div>

                    {templates.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {templates.map(template => (
                                <button
                                    key={template.id}
                                    onClick={() => handleSelectTemplate(template)}
                                    className="p-4 border border-gray-200 rounded-lg text-left hover:border-blue-500 hover:bg-blue-50 transition-colors"
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <span className="text-xs text-gray-500 uppercase">{template.template_type_display}</span>
                                            <h3 className="font-medium text-gray-900">{template.name}</h3>
                                        </div>
                                        <span className="text-xs text-gray-400">{template.jurisdiction}</span>
                                    </div>
                                    <div className="mt-2 text-sm text-gray-500">
                                        {template.variable_count} variables • v{template.version}
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <p>No templates available</p>
                            <button
                                onClick={installSampleTemplates}
                                className="mt-2 text-blue-600 hover:underline"
                            >
                                Install sample templates to get started
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Step 2: Fill Variables */}
            {step === 2 && selectedTemplate && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="mb-6">
                        <button
                            onClick={() => setStep(1)}
                            className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to Templates
                        </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Form */}
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Document Title *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    rows="2"
                                ></textarea>
                            </div>

                            <hr />

                            <h3 className="font-medium text-gray-900">Template Variables</h3>

                            {selectedTemplate.variables_schema?.map(variable => (
                                <div key={variable.name}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {variable.label}
                                        {variable.required && <span className="text-red-500 ml-1">*</span>}
                                    </label>
                                    {variable.type === 'date' ? (
                                        <input
                                            type="date"
                                            value={formData.variable_values[variable.name] || ''}
                                            onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        />
                                    ) : variable.type === 'number' || variable.type === 'currency' ? (
                                        <input
                                            type="number"
                                            value={formData.variable_values[variable.name] || ''}
                                            onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                            step={variable.type === 'currency' ? '0.01' : '1'}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={formData.variable_values[variable.name] || ''}
                                            onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        />
                                    )}
                                </div>
                            ))}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                                <select
                                    value={formData.output_format}
                                    onChange={(e) => setFormData({ ...formData, output_format: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="PDF">PDF</option>
                                    <option value="DOCX">Word Document</option>
                                    <option value="HTML">HTML</option>
                                </select>
                            </div>

                            <button
                                onClick={handlePreview}
                                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                            >
                                Preview Document →
                            </button>
                        </div>

                        {/* Template Info */}
                        <div className="bg-gray-50 rounded-lg p-6">
                            <h3 className="font-semibold text-gray-900 mb-4">{selectedTemplate.name}</h3>
                            <p className="text-sm text-gray-600 mb-4">{selectedTemplate.description}</p>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Type:</span>
                                    <span className="text-gray-900">{selectedTemplate.template_type_display}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Jurisdiction:</span>
                                    <span className="text-gray-900">{selectedTemplate.jurisdiction_display}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Version:</span>
                                    <span className="text-gray-900">{selectedTemplate.version}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 3: Preview & Generate */}
            {step === 3 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="mb-6 flex items-center justify-between">
                        <button
                            onClick={() => setStep(2)}
                            className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to Edit
                        </button>
                        <button
                            onClick={handleGenerate}
                            disabled={loading}
                            className={`px-6 py-2 rounded-lg text-white font-medium ${loading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
                                }`}
                        >
                            {loading ? 'Generating...' : 'Generate Document'}
                        </button>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 text-sm font-medium text-gray-700">
                            Document Preview
                        </div>
                        <div
                            className="p-6 min-h-96 bg-white prose max-w-none"
                            dangerouslySetInnerHTML={{ __html: preview }}
                        ></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateDocument;
